import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ProgramsComparison() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: cardsRef, isVisible: cardsVis } = useScrollAnimation({ threshold: 0.1 });

  const onSitePoints = [
    { icon: 'ri-building-line', text: 'Professional studio and classroom facilities' },
    { icon: 'ri-team-line', text: 'Community environment with other creatives' },
    { icon: 'ri-music-2-line', text: 'Full equipment suite on-site' },
    { icon: 'ri-moon-line', text: 'Evening and night classes available' },
    { icon: 'ri-shield-check-line', text: 'Structured, focused learning environment' },
    { icon: 'ri-calendar-2-line', text: 'Flexible weekly scheduling options' },
  ];

  const inHomePoints = [
    { icon: 'ri-home-heart-line', text: 'Sessions in your own comfortable space' },
    { icon: 'ri-briefcase-line', text: 'All equipment brought to you' },
    { icon: 'ri-user-line', text: 'Fully personalized one-on-one attention' },
    { icon: 'ri-heart-3-line', text: 'Ideal for neurodivergent learners' },
    { icon: 'ri-parent-line', text: 'Great for children and all ages' },
    { icon: 'ri-map-pin-2-line', text: 'Available based on location and scheduling' },
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-yellow/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-purple/8 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-8">
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-blue/8 text-brand-blue-dark font-body text-xs font-semibold uppercase tracking-widest mb-5`}>
            Find Your Fit
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl font-black text-brand-blue-dark mb-4`}>
            On-Site or <span className="gradient-text">In-Home?</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-xl mx-auto leading-relaxed`}>
            Both paths lead to excellence. The difference is simply where and how you learn best.
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* On-Site */}
          <div className={`scroll-hidden slide-left ${cardsVis ? 'scroll-visible' : ''} rounded-3xl overflow-hidden card-lift`}>
            <div className="relative h-52 overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/46cdcbbfe05118c16c53e0f39722e60e.jpg"
                alt="On-Site Programs at Creative Institute"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/80 via-[#1E3A8A]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-7">
                <span className="inline-block px-3 py-1 rounded-full bg-brand-yellow text-brand-blue-dark font-body text-[10px] font-bold uppercase tracking-wide mb-2">At Our Facility</span>
                <h3 className="font-heading text-white text-2xl font-black">On-Site Programs</h3>
              </div>
            </div>
            <div className="bg-white p-7">
              <ul className="space-y-3 mb-7">
                {onSitePoints.map((pt) => (
                  <li key={pt.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-blue/8 text-brand-blue-dark flex-shrink-0">
                      <i className={`${pt.icon} text-sm`} />
                    </div>
                    <p className="font-body text-gray-600 text-sm">{pt.text}</p>
                  </li>
                ))}
              </ul>
              <Link
                to="/on-site-programs"
                className="whitespace-nowrap block w-full py-3.5 rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#3730A3] text-white font-body font-bold text-sm text-center hover:opacity-90 transition-opacity cursor-pointer"
              >
                Explore On-Site Programs
              </Link>
            </div>
          </div>

          {/* In-Home */}
          <div className={`scroll-hidden slide-right ${cardsVis ? 'scroll-visible' : ''} rounded-3xl overflow-hidden card-lift`}>
            <div className="relative h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=cozy%20warm%20home%20music%20lesson%20setup%2C%20keyboard%20and%20guitar%20in%20a%20comfortable%20living%20room%20space%2C%20soft%20warm%20golden%20lighting%2C%20personal%20and%20intimate%20creative%20learning%20environment%2C%20welcoming%20home%20atmosphere%2C%20royal%20purple%20and%20gold%20accents%2C%20no%20people%2C%20high%20quality%20photography&width=600&height=300&seq=inhome-compare-001&orientation=landscape"
                alt="In-Home Programs Creative Institute"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#5B21B6]/80 via-[#5B21B6]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-7">
                <span className="inline-block px-3 py-1 rounded-full bg-brand-yellow text-brand-blue-dark font-body text-[10px] font-bold uppercase tracking-wide mb-2">Your Space</span>
                <h3 className="font-heading text-white text-2xl font-black">In-Home Programs</h3>
              </div>
            </div>
            <div className="bg-white p-7 border border-[#E0E7FF]">
              <ul className="space-y-3 mb-7">
                {inHomePoints.map((pt) => (
                  <li key={pt.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-purple/8 text-brand-purple flex-shrink-0">
                      <i className={`${pt.icon} text-sm`} />
                    </div>
                    <p className="font-body text-gray-600 text-sm">{pt.text}</p>
                  </li>
                ))}
              </ul>
              <Link
                to="/in-home-programs"
                className="whitespace-nowrap block w-full py-3.5 rounded-xl bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] text-white font-body font-bold text-sm text-center hover:opacity-90 transition-opacity cursor-pointer"
              >
                Explore In-Home Programs
              </Link>
            </div>
          </div>
        </div>

        <div className={`scroll-hidden delay-400 ${cardsVis ? 'scroll-visible' : ''} text-center mt-10`}>
          <p className="font-body text-gray-400 text-sm">
            Not sure which is right for you?{' '}
            <Link to="/contact" className="text-brand-purple font-semibold hover:underline cursor-pointer">
              Contact us and we will help you decide.
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
